import { useEffect, useRef, useState } from 'react';

interface PullState {
  pull: number;
  refreshing: boolean;
}

const THRESHOLD = 70;
const MAX = 110;

export function usePullToRefresh(onRefresh: () => Promise<void> | void): PullState {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startYRef = useRef<number | null>(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0 || refreshingRef.current) return;
      // Si el touch nace dentro de un elemento marcado como
      // [data-no-pull-refresh] (ej. el contenedor del mapa de Estadísticas),
      // ignoramos el gesto. Sin esto, un pinch-to-zoom sobre el mapa
      // arrastrando dos dedos hacia abajo dispara el pull y refresca todo.
      const target = e.target as Element | null;
      if (target?.closest?.('[data-no-pull-refresh]')) return;
      startYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null) return;
      // Si el usuario hace scroll real, abortamos el pull
      if (window.scrollY > 0) {
        startYRef.current = null;
        pullRef.current = 0;
        setPull(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy > 5) {
        const damped = Math.min(dy * 0.5, MAX);
        pullRef.current = damped;
        setPull(damped);
        if (e.cancelable) e.preventDefault();
      }
    };

    const onTouchEnd = async () => {
      const reached = pullRef.current >= THRESHOLD;
      startYRef.current = null;
      pullRef.current = 0;
      setPull(0);
      if (reached && !refreshingRef.current) {
        refreshingRef.current = true;
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          refreshingRef.current = false;
          setRefreshing(false);
        }
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    // ── Refresh automático al volver de background ────────────────────────
    // Cuando la app vuelve a primer plano tras haber estado oculta al menos
    // BG_REFRESH_THRESHOLD_MS, disparamos el mismo refresh que el pull
    // gestual — incluido el indicador visual. 10 min evita ruido en
    // cambios rápidos de app y refresca sólo cuando realmente "vuelves" a
    // la app tras un rato.
    const BG_REFRESH_THRESHOLD_MS = 10 * 60 * 1000;
    let hiddenAt: number | null = null;

    const triggerBgRefresh = async () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      setRefreshing(true);
      try { await onRefresh(); }
      finally {
        refreshingRef.current = false;
        setRefreshing(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;
      const wasHiddenFor = hiddenAt == null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (wasHiddenFor >= BG_REFRESH_THRESHOLD_MS) {
        void triggerBgRefresh();
      }
    };

    // pageshow con persisted=true cubre la restauración desde BFCache en
    // iOS Safari, donde a veces visibilitychange no llega de forma fiable.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void triggerBgRefresh();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [onRefresh]);

  return { pull, refreshing };
}

export function PullIndicator({ pull, refreshing }: PullState) {
  const visible = pull > 0 || refreshing;
  if (!visible) return null;
  const height = refreshing ? 60 : pull;
  const progress = Math.min(pull / THRESHOLD, 1);
  const ready = pull >= THRESHOLD;
  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          background: refreshing ? 'rgba(24, 114, 54, 0.95)' : 'rgba(24, 114, 54, 0.08)',
          color: refreshing ? 'var(--text-on-brand)' : 'var(--brand-primary)',
          zIndex: 9998,
          pointerEvents: 'none',
          transition: refreshing ? 'height 150ms ease-out, background 150ms ease-out' : 'none',
          boxShadow: refreshing ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
        }}
      >
        {refreshing ? (
          <>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '3px solid rgba(255,255,255,0.3)',
                borderTopColor: 'var(--text-on-brand)',
                animation: 'ptr-spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.3 }}>Cargando...</span>
          </>
        ) : (
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            style={{
              transform: `rotate(${ready ? 180 : progress * 180}deg)`,
              transition: 'transform 120ms ease-out',
              opacity: 0.4 + progress * 0.6,
            }}
          >
            <path fill="currentColor" d="M11 5v11.17l-4.88-4.88a1 1 0 0 0-1.41 1.42l6.59 6.58a1 1 0 0 0 1.41 0l6.59-6.58a1 1 0 1 0-1.41-1.42L13 16.17V5a1 1 0 0 0-2 0z"/>
          </svg>
        )}
      </div>
      {/* keyframes inline para evitar tener que tocar CSS global */}
      <style>{'@keyframes ptr-spin { to { transform: rotate(360deg); } }'}</style>
    </>
  );
}
